<template>
  <div class="step-address">
    <form
      class="form form--large"
      @invalid.capture.prevent="handleInvalid"
      @submit.prevent="handleSubmit"
      @change="handleChange"
    >
      <fieldset
        v-if="cardAllowed"
        class="fieldset fieldset--add-card"
      >
        <legend class="legend">
          {{ $t("address.add-card") }}
        </legend>

        <img
          class="left card-icon"
          :src="`${publicPath}img/${$t('address.card-icon')}`"
          width="65"
          height="53"
        >
        <div class="checkbox">
          <input
            id="truth"
            v-model="fullfillCard"
            class="checkbox__input"
            type="checkbox"
            name="mail-card"
            value="truth"
          >
          <label
            class="checkbox__label"
            for="truth"
          >
            <span>
              {{ $t("address.send-card") }}
              <span
                v-if="passType.cardCost"
              >for {{ passType.cardCost | currency }}</span>
            </span>
          </label>
        </div>
        <p class="form__note">
          {{ $t("address.card-note-1") }}
        </p>
        <p class="form__note">
          {{ $t("address.card-note-2") }}
        </p>
      </fieldset>
      <transition
        name="slide-down"
        mode="in-out"
      >
        <fieldset
          v-if="requireAddress || fullfillCard"
          class="fieldset fieldset--address"
        >
          <legend
            class="legend"
            :class="{ 'legend--small': !requireAddress }"
          >
            Mailing address
          </legend>
          <Input
            id="mailing-address-1"
            v-model="form.addressStreet"
            label="Street address 1"
            error-code="UserAddressStreetInvalid"
            autocomplete="street-address address-line1"
            :error="formErrors['mailing-address-1']"
            required
          />

          <Input
            id="mailing-address-2"
            v-model="form.addressStreet2"
            label="Street address 2"
            error-code="UserAddressStreet2Invalid"
            hint="(optional)"
            autocomplete="street-address address-line2"
          />
          <div class="form__row">
            <div class="form__column form__column--city">
              <Input
                id="city"
                v-model="form.addressCity"
                label="City"
                error-code="UserAddressCityInvalid"
                autocomplete="address-level2"
                :error="formErrors.city"
                required
              />
            </div>
          </div>

          <div class="form__row">
            <div class="form__column form__column">
              <Input
                v-if="addressIsInternational"
                id="state"
                v-model="form.addressState"
                label="State/province"
                error-code="UserAddressStateInvalid"
                autocomplete="address-level1"
                :error="formErrors.state"
                required
              />
              <div v-else>
                <label
                  class="label"
                  for="state"
                >State</label>
                <select
                  id="state"
                  v-model="form.addressState"
                  class="select"
                  :class="formErrors.state ? 'input--error' : ''"
                  name="state"
                  autocomplete="address-level1"
                  required
                >
                  <option :value="null">
                    Select
                  </option>
                  <option
                    v-for="{ name, abbreviation } in states"
                    :key="abbreviation"
                    :value="abbreviation"
                  >
                    {{ name }}
                  </option>
                </select>
                <p
                  v-if="formErrors.state"
                  class="form__error"
                >
                  {{ formErrors.state }}
                </p>
              </div>
            </div>
            <div class="form__column">
              <Input
                id="zip"
                v-model="form.zipCode"
                :label="addressIsInternational ? 'ZIP/postal code' : 'ZIP code'"
                error-code="UserZipcodeInvalid"
                autocomplete="postal-code"
                :error="formErrors.zip"
                required
              />
            </div>
          </div>
          <div class="form__row">
            <div class="form__column">
              <label
                class="label"
                for="country"
              >Country</label>
              <select
                id="country"
                v-model="form.countryId"
                class="select"
                :class="formErrors.country ? 'input--error' : ''"
                name="country"
                autocomplete="address-level1"
                required
              >
                <option :value="null">
                  Select
                </option>
                <option
                  v-for="country in countries"
                  :key="country.countryId"
                  :value="country.countryId"
                >
                  {{ country.countryName }}
                </option>
              </select>
            </div>
          </div>
        </fieldset>
      </transition>
      <div class="navigation-buttons">
        <input
          type="submit"
          class="button button--submit"
          :disabled="!isValid"
          value="Continue"
        >
        <PreviousStepButton />
      </div>
      <p
        v-if="
          !requireAddress && fullfillCard && registration.fullfillCard !== true
        "
        class="continue-link"
      >
        <router-link :to="{ name: 'payment' }">
          {{
            $t("address.continue-without-card")
          }}
        </router-link>
      </p>
    </form>
  </div>
</template>
<script>
import Input from '../components/Input.vue';
import PreviousStepButton from '../components/PreviousStepButton.vue';
import countries from '../bcycle-countries';
import states from '../states.json';

export default {
  components: { Input, PreviousStepButton },
  data() {
    const { registration, user } = this.$store.state;
    const { cardDefault } = this.$store.getters.passType;
    const fullfillCard = registration.fullfillCard
      ? registration.fullfillCard
      : cardDefault;

    // eslint-disable-next-line no-underscore-dangle
    const _countryName = countries.find(c => c.countryId === user.countryId).countryName;

    return {
      fullfillCard,
      form: {
        addressStreet: user.addressStreet,
        addressStreet2: user.addressStreet2,
        addressCity: user.addressCity,
        addressState: user.addressState.length <= 2 ? user.addressState : user.addressState.replace(`, ${_countryName}`, ''),
        zipCode: user.zipCode,
        countryId: user.countryId || 236,
      },
      formErrors: {},
      publicPath: process.env.BASE_URL,
      countries,
      states,
    };
  },
  computed: {
    registration() {
      return this.$store.state.registration;
    },
    passType() {
      return this.$store.getters.passType;
    },
    requireAddress() {
      return this.$store.state.requireAddress;
    },
    cardAllowed() {
      return this.$store.getters.passType.cardDefault !== null;
    },
    isValid() {
      // form is valid if all address fields are entered,
      // or if address is not required and a card isn't requested
      return (
        (!this.fullfillCard && !this.requireAddress)
        || (this.form.addressStreet !== ''
          && this.form.lastName !== ''
          && this.form.addressCity !== ''
          && this.form.addressState !== null
          && this.form.zipCode !== '')
      );
    },
    addressIsInternational() {
      return this.form.countryId !== 236;
    },
    countryName() {
      return countries.find(c => c.countryId === this.form.countryId).countryName;
    },
  },
  watch: {
    addressIsInternational(newVal, oldVal) {
      if (oldVal === false && newVal === true) {
        this.form.addressState = '';
      }
    },
  },
  methods: {
    handleInvalid(e) {
      this.formErrors = {
        ...this.formErrors,
        [e.target.name]: e.target.validationMessage,
      };
    },
    handleChange(e) {
      this.formErrors = {
        ...this.formErrors,
        [e.target.name]: '',
      };
    },
    handleSubmit() {
      if (!this.isValid) {
        return;
      }
      this.$store.dispatch('updateRegistration', {
        fullfillCard: this.fullfillCard || false,
      });

      let userData = this.form;
      if (this.addressIsInternational) {
        userData = {
          ...this.form,
          addressState: `${this.form.addressState.replace(`, ${this.countryName}`, '')}, ${this.countryName}`,
        };
      }
      this.$store.dispatch('updateUser', userData);

      this.$router.push({
        name: this.$store.getters.nextStep(this.$route.name),
      });
    },
  },
};
</script>
